import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Observable, of, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';

import { HTTP_STATUS_CODES } from '../app-constants/http-status-codes';
import { AuthenticationService } from '../authentication';
import { Logger } from '../logger/services/logger.service';
import { AppNavigationService, DefaultPageRouteService, RouterService } from '../navigation';

import { UrlService } from './url.service';

import { environment } from 'env/environment';

@UntilDestroy()
@Injectable()
export class RequestInterceptor implements HttpInterceptor {
  public constructor(
    private authService: AuthenticationService,
    private readonly urlService: UrlService,
    private readonly routerService: RouterService,
    private readonly defaultRoutingService: DefaultPageRouteService,
    private readonly logger: Logger,
    private readonly appNavigationService: AppNavigationService) {
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const coreApiUrl = this.urlService.buildServerApiUrl();
    let modifiedReq = request;
    if (request.url.startsWith(coreApiUrl) && environment.clientVersion !== null) {
      modifiedReq = request.clone({
        headers: request.headers.set('X-KH-Client-Version', environment.clientVersion)
      });
    }

    return next.handle(modifiedReq)
      .pipe(catchError(error => this.handleErrors(request, error)));
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public handleErrors(request: HttpRequest<any>, error: HttpErrorResponse): Observable<any> {
    if (error instanceof HttpErrorResponse) {
      if (error.status === HTTP_STATUS_CODES.UNAUTHORIZED) {
        if (error.error?.error !== 'invalid_grant') {
          this.logger.error(`Navigation to ${this.appNavigationService.getLastNavigation()} failed`, error);
          // if error is invalid_grant then user is in the process of logging in, so no need to handle log out here
          this.redirectOnUnauthorized();

          return of();
        }
      }

      if (error.status === HTTP_STATUS_CODES.FORBIDDEN) {
        this.logger.error(`Navigation to ${this.appNavigationService.getLastNavigation()} failed`, error);
        this.authService.isAuthorized()
          .pipe(untilDestroyed(this))
          .subscribe(isAuthorized => {
            if (isAuthorized) {
              this.authService.setInterruptedUrl(this.routerService.getUrl());
            }

            this.defaultRoutingService.navigateToForbidden();
          });

        return of();
      }
    }

    return throwError(error);
  }

  public redirectOnUnauthorized(): void {
    this.authService.logOut();
    this.defaultRoutingService.navigateToPublicFallback();
  }
}
